/**
 * The desk Merlin names at the end of a step or a checklist item — "clear
 * SHERW01 credit hold (Laura)", "… (Cliff Platt, back 10/5)" — and the text
 * without it. Shared by the thread brief's Do next (merlinBrief.doNextByDesk)
 * and the case file's outstanding items (caseTaskAsks), which are seeded
 * from the same proposal and keep the parenthetical.
 */

/** The parentheses Merlin ends a step with: "… (Jess)", "… (Cliff Platt, back 10/5)". */
const TRAILING_PARENS = /\s*\(([^()]{2,64})\)\s*[.;]?\s*$/;

/**
 * The desk named at the end of a step and the step without it. The desk is
 * what stands before the first comma, and must carry no digit, so a trailing
 * "(F2015.50)" or "(10/6)" is not a desk. A remark after the comma ("back
 * 10/5") stays on the step.
 */
export function splitDesk(text: string): { desk: string; text: string } {
  const match = TRAILING_PARENS.exec(text);
  if (!match) return { desk: '', text };
  const [head, ...rest] = match[1].split(',');
  const desk = head.trim();
  if (desk.length < 2 || /\d/.test(desk)) return { desk: '', text };
  const remark = rest.join(',').trim();
  const body = text.slice(0, match.index).trimEnd();
  return { desk, text: remark ? `${body} (${remark})` : body };
}

/** "QC lab (Jake/JB): pull retain…" (or "… — ON HOLD"): the desk and the
 *  people in it, said first. */
const LEADING_DESK = /^\s*[^:()]{2,40}\(([^()]{2,40})\)\s*[:\u2014\u2013]/;
/** A name as people write it ("Jake", "Ashton", "Cliff Platt"), not initials ("JB", "MAO"). */
const PERSON = /^[A-Z][a-z]+(?: [A-Z][a-z]+)?$/;

/**
 * The person named at the start of a checklist item, "QC lab (Jake/JB): …"
 * → "Jake", "Customer service (MAO/Ashton): …" → "Ashton" (UX round
 * 2026-10-13: Merlin seeds teammates' items that way and tags them "us",
 * so they had no Ask button). '' when no person is named there.
 */
export function leadingDesk(text: string): string {
  const match = LEADING_DESK.exec(text);
  if (!match) return '';
  return (
    match[1]
      .split(/[/,&]| and /)
      .map((p) => p.trim())
      .find((p) => PERSON.test(p)) ?? ''
  );
}

/** Words that mean the customer's end of the line when they stand alone as
 *  a desk ("… (customer)", "… (the branch)"); shared with the Ask modal's
 *  recipient reading (askTargetsView). */
export const CUSTOMER_DESK_WORDS: ReadonlySet<string> = new Set([
  'customer',
  'the customer',
  'end customer',
  'customer contact',
  'the branch',
  'branch',
  'jobsite',
  'the jobsite',
  'distributor',
  'the distributor',
]);

/**
 * True when a desk IS the customer — "(customer)", "(the branch)" — with
 * nobody else on it. "Cliff / customer" is Cliff's desk (he carries the
 * customer's part), not the customer's. Quality never asks the customer's
 * desk from the door (kanban d3915b86).
 */
export function isCustomerDesk(desk: string): boolean {
  return CUSTOMER_DESK_WORDS.has(desk.trim().toLowerCase().replace(/\s+/g, ' '));
}
